import { useQuery, useQueryClient } from "@tanstack/react-query";

import { getEmployerIdentity } from "../api/services/employerJobs";
import { useEmployerJobs } from "./useEmployerJobs";
import { useAccountRole } from "../context/RoleContext";
import { useSuperApp } from "./useSuperApp";

const EMPLOYER_IDENTITY_KEY = ["employerIdentity"];

// The company an employer just registered, remembered so "Манай компани → Харах" opens it
// before their first ad exists. The only server-side record of membership is the ads a
// person has posted, so a register-joined employer (whose phone is not the one on the
// company row) gets no companyId back from /diil/employer/me until they publish. This
// bridges that gap; once an ad exists the server value takes over and this is ignored.
// Keyed by phone so switching accounts on one device cannot cross the wires.
const STORED_COMPANY_KEY = "diil_employer_company";

const readStoredCompany = (phone) => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORED_COMPANY_KEY) || "null");
    if (!raw || !raw.companyId) return null;
    // A record written under a different phone belongs to another account on this device.
    if (phone && raw.phone && raw.phone !== phone) return null;
    return { companyId: raw.companyId, companyName: raw.companyName || null };
  } catch {
    return null;
  }
};

const writeStoredCompany = (phone, company) => {
  const companyId = company?.id ?? company?.companyId ?? null;
  if (!companyId) return;
  try {
    localStorage.setItem(
      STORED_COMPANY_KEY,
      JSON.stringify({
        phone: phone || null,
        companyId,
        companyName: company?.name ?? company?.companyName ?? null,
      })
    );
  } catch {
    // Private mode or quota — the server/ad fallbacks still apply, just not across a reload.
  }
};

/**
 * Which company the signed-in employer belongs to.
 *
 * /diil/employer/me answers this, but only once the employer has been attached to a
 * company — which happens through resolveCompany() after a registration-number lookup.
 * Until then `companyId` is null and `needsRegistration` is true.
 *
 * Gated on the role so a jobseeker or a signed-out visitor never fires an authenticated
 * request; the API client treats a 401 as an expired session and would sign them out of
 * nothing.
 *
 * @param {Object} [options]
 * @param {boolean} [options.probe] ask even while the account is a jobseeker — still only
 *   when signed in. The header's profile menu does, while it is open, to say "Компани
 *   бүртгүүлэх" instead of "Ажил олгогч" BEFORE the role is switched rather than after.
 */
export const useEmployerCompany = ({ probe = false } = {}) => {
  const queryClient = useQueryClient();
  const { accountRole } = useAccountRole();
  const { isAuthenticated, currentUser } = useSuperApp();
  const enabled = isAuthenticated && (accountRole === "employer" || probe);
  const phone = (currentUser?.phone || "").replace(/\D/g, "") || null;

  const query = useQuery({
    queryKey: EMPLOYER_IDENTITY_KEY,
    enabled,
    queryFn: async () => {
      // getEmployerIdentity traces the round trip itself — see `traced` in
      // services/employerJobs.js. It never throws, so a failure has to be turned into
      // one here for react-query to record it as an error rather than as "no company".
      const res = await getEmployerIdentity();
      if (!res?.success) {
        const err = new Error(res?.error || "Байгууллагын мэдээлэл татахад алдаа гарлаа");
        err.status = res?.status ?? 0;
        throw err;
      }

      // The prompt this drives depends entirely on this payload's shape, so say so when
      // the request succeeded but carried no company either way.
      //
      // Never for an admin: they have no company by design — they publish for many
      // businesses and belong to none — so warning about it on every page load buried the
      // real cases it was written to catch.
      if (!res.data?.companyId && !res.data?.platformStaff && !res.data?.admin) {
        console.warn(
          "[useEmployerCompany] /diil/employer/me answered without a companyId:",
          res.data
        );
      }
      return res.data || {};
    },
  });

  // Fallback: /diil/employer/me is the documented source of companyId but has never been
  // exercised by this app, so if it comes back without one, take it off an ad the employer
  // has already published — those definitely carry the company they were posted under.
  const { posts, isLoading: adsLoading } = useEmployerJobs({ enabled });
  const companyIdFromAds = posts?.find((p) => p.companyId)?.companyId ?? null;

  const admin = !!query.data?.admin;

  // The company this account last registered on this device, if any. Only a fallback for a
  // register-joined employer who has not posted yet — see STORED_COMPANY_KEY. Never for an
  // admin, who belongs to no company by design.
  const stored = enabled && !admin ? readStoredCompany(phone) : null;

  // An admin belongs to no company (the server answers with none, on purpose), and the
  // ad fallback must not invent one: picking the first ad's company would pin them to
  // whichever business they happened to post for first and hide all the others.
  // The stored company is the last resort, below both server sources so a real answer
  // always wins over the local memory.
  const companyId = query.data?.companyId ?? (admin ? null : companyIdFromAds) ?? stored?.companyId ?? null;
  const platformStaff = !!query.data?.platformStaff;

  // This account's own ads, grouped under the company each was published for. An admin
  // posts for many businesses, so a flat list of ads reads as a jumble — the company is
  // what tells two "Ресепшн ажилд авна" apart. Ads arrive newest first (listMyJobs orders
  // by id desc), so the groups come out most-recently-posted first without sorting.
  const myCompanyGroups = [];
  const groupIndex = new Map();
  for (const post of posts || []) {
    const key = String(post.companyId ?? "none");
    if (!groupIndex.has(key)) {
      groupIndex.set(key, myCompanyGroups.length);
      myCompanyGroups.push({ companyId: post.companyId ?? null, companyName: post.companyName || "—", posts: [] });
    }
    myCompanyGroups[groupIndex.get(key)].posts.push(post);
  }
  // Both sources have to settle before "no company" is a fact rather than a race —
  // answering early would prompt an employer who already has one to register again.
  const isLoading = enabled && (query.isLoading || adsLoading);

  // A request that failed tells us nothing about whether a company exists. Treating
  // the empty result as "none" turned every expired token or 500 into a prompt to
  // register a company the employer may well already have.
  const answered = query.isSuccess;

  // The failure itself is already logged by `traced`; this says what the screen does
  // about it, which is the part that surprises people.
  if (enabled && query.isError) {
    console.warn(
      "[useEmployerCompany] company state unknown, showing retry instead of the register prompt:",
      query.error?.message
    );
  }

  return {
    companyId,
    companyName: query.data?.companyName || stored?.companyName || null,
    companyPhoto: query.data?.companyPhoto || null,
    platformStaff,
    /**
     * The caller posts as a Diil admin — for many different companies, under a different
     * contact phone each time, and belonging to none of them. Read only from the identity
     * response, like `selfServe`, so a failed /me hides the affordance rather than showing
     * one the server would refuse.
     */
    admin,
    /**
     * The company an admin's own account belongs to, when they also work somewhere.
     *
     * Deliberately not folded into `companyId`: an admin belongs to none of the businesses
     * they publish for, and this is offered as a choice on the wizard's last step rather
     * than treated as their company. Nothing else on this hook reads it.
     */
    ownCompanyId: query.data?.ownCompanyId ?? null,
    ownCompanyName: query.data?.ownCompanyName || null,
    /** This account's own ads, grouped by the company each was published for. */
    myCompanyGroups,
    /**
     * The company page to open from "Засах" / the sidebar. Normally the employer's own
     * company; for an admin, who has none, the one they most recently posted for — that
     * page then carries the rest of their ads, grouped by company.
     *
     * `ownCompanyId` closes the last gap: an admin who has published nothing yet had no
     * company on either source and was dropped back on Профайл, which looked like the
     * button did nothing. Their own account's company still has a page to land on.
     */
    landingCompanyId: companyId ?? myCompanyGroups[0]?.companyId ?? query.data?.ownCompanyId ?? null,
    /** How many ads this account has published, across every company. */
    myPostCount: (posts || []).length,
    /** The company is one Diil owns — not a real customer's. Says nothing about who may edit it. */
    selfServe: !!query.data?.selfServe,
    /**
     * This account may edit its company's page.
     *
     * Read only from the identity response, never from the ad fallback: it has to come
     * from the same resolution the edit endpoints authorize against. If /me failed this
     * stays false and the edit affordances simply don't appear — the safe direction.
     *
     * Narrower than `selfServe`, and the difference is a real person: someone an admin's
     * second ad brought into a company is recorded against it as a colleague, reads
     * `selfServe` true, and cannot edit it — the server answers their save with a 404.
     */
    canEditCompany: !!query.data?.canEditCompany,
    isLoading,
    /**
     * Only the identity request still in flight — not the ads behind it. Enough to know
     * whether this is an admin or has a company; the ad list (an admin's is every admin's
     * ads) can take much longer and only matters for the ad-derived company fallback.
     */
    identityLoading: enabled && query.isLoading,
    /** The identity request failed, so company state is unknown rather than absent. */
    isUnknown: enabled && !isLoading && !answered && !companyId,
    error: query.error || null,
    /**
     * True once we know for certain there is no company to show yet.
     *
     * Never for an admin: they have no company by design, so the register prompt would
     * be asking them to fix something that is not broken.
     */
    needsRegistration: enabled && !isLoading && answered && !platformStaff && !admin && !companyId,
    refresh: () => queryClient.invalidateQueries({ queryKey: EMPLOYER_IDENTITY_KEY }),
    /**
     * Adopt the company that resolveCompany just returned.
     *
     * Writing it straight into the cache rather than only refetching: /diil/employer/me
     * is a separate read, and if it doesn't reflect the new attachment yet the screen
     * would sit there still asking the employer to register a company they just
     * registered. The refetch afterwards reconciles with whatever the server says.
     */
    applyResolved: (company) => {
      if (!company) return;
      // Persist first: the invalidate below refetches /diil/employer/me, which for a
      // register-joined employer answers with no companyId and would otherwise wipe the
      // value straight back out of the cache. The stored copy survives that and keeps
      // "Манай компани → Харах" pointing at the company until the first ad exists.
      writeStoredCompany(phone, company);
      queryClient.setQueryData(EMPLOYER_IDENTITY_KEY, (prev) => ({
        ...(prev || {}),
        companyId: company.id ?? company.companyId ?? prev?.companyId ?? null,
        companyName: company.name ?? company.companyName ?? prev?.companyName ?? null,
      }));
      queryClient.invalidateQueries({ queryKey: EMPLOYER_IDENTITY_KEY });
    },
  };
};

export default useEmployerCompany;
